/**
 * Column definitions for Team / Users tables.
 *
 * Single source of truth — the table reads these to render rows, Print uses
 * them for WYSIWYG rendering, and Excel export reads `renderText` to produce
 * cell strings. Adding or hiding a column happens here once.
 *
 * - **Composite Employment Status cell.** The status pill is followed by
 *   small inline icons for Auto Sync mode and login-record presence.
 * - **Header split.** Employment Status (Employee record) and User Status
 *   (User login record) are distinct concerns and get distinct columns.
 * - **Per-scope default visibility.** Each column declares which filter
 *   scopes it's relevant for. The screen recomputes default visibility
 *   whenever the filter scope changes (Active / Not Active / drift kind).
 *   The first 5 columns (Status, Name, Job Title, EmployeeId, Role) are
 *   always visible; everything after that is dynamic.
 *
 * Each column declares:
 *   - id                  — stable identifier, used for sort keys + column-visibility state
 *   - labelKey            — store_settings i18n key (resolved by the consumer)
 *   - width               — fixed pixel width (omit for flex)
 *   - flex                — flex weight if width is undefined
 *   - sortFn              — comparator for two members; omit for non-sortable
 *   - renderCell          — table-cell JSX (uses _table-shared helpers)
 *   - renderText          — plain-string projection for Print / Excel export
 *   - alwaysVisible       — true = user can't hide it, and the scope-driven
 *                           default-visible computation always includes it
 *   - defaultForScopes    — which filter scopes show this column by default
 */
import React from 'react';
import { Box, HStack, VStack } from 'native-base';
import { RefreshCwIcon, ShieldUserIcon, StarIcon, Text, UserRoundCheckIcon, UserRoundXIcon } from '@pimm/base';
import {
  Badge,
  MutedText,
  RowText,
  YesNoBadge,
  formatBirthday,
  formatDateShort,
  formatRelativeDays,
  hasOverduePurge,
  hasStaleLogin,
  hasStaleSchedule,
  importSyncIconColor,
  roleBadgeStyle,
  statusBadgeStyle,
  userStatusBadgeStyle,
} from './team-management-table-shared';
import type { DriftKind, ImportSyncMode, TeamMember, TeamMemberRole, TeamMemberStatus, UserLoginStatus } from '../types';
import { RECOVERY_EMAIL_STATUS_VERIFIED } from '../types';

// Strings the column renderers + comparators need at runtime. Passed in by the
// consumer (TeamManagementView) since columns.tsx itself can't call hooks.
export type ColumnContext = {
  statusLabels: Record<TeamMemberStatus, string>;
  roleLabels: Record<TeamMemberRole, string>;
  userStatusLabels: Record<UserLoginStatus, string>;
  yesLabel: string;
  noLabel: string;
  loginBadgeLabel: string;
  autoSyncOnLabel: string; // "On" — Auto Sync mode default state
  autoSyncProtectedLabel: string; // "Status/Role Only" — partial freeze
  autoSyncOffLabel: string; // "Off" — full freeze
  autoSyncPrefixLabel: string; // "Importer Sync" — prefix for the composite status cells' sync line
  primaryLabel: string;
  emptyValue: string;
  // Live translate function — needed (rather than a pre-resolved string) by
  // formatRelativeDays, since it interpolates a different count per row.
  translate: (key: string, options?: Record<string, unknown>) => string;
  // Site's UTC offset (minutes) — formatRelativeDays and the stale-date drift
  // predicates (hasStaleLogin/hasStaleSchedule/hasOverduePurge) diff calendar
  // days in the store's local time, not the viewing device's, matching
  // team-management-importer-connection.tsx's site-local time handling.
  utcOffsetInMinutes: number;
};

// Filter scopes drive default column visibility. Maps 1:1 with the
// StatusFilterValue union — see types.ts.
export type FilterScope = 'active' | 'notActive' | `drift:${DriftKind}`;

// Which underlying record the column reads from. Surfaced in the Columns
// popover as a small "User" tag next to user-sourced columns so a manager
// can see at-a-glance which fields will be empty for crew (no login record).
// Defaults to 'employee' when omitted.
export type ColumnSource = 'employee' | 'user';

export type ColumnDef = {
  id: string;
  labelKey: string;
  width?: number;
  flex?: number;
  sortFn?: (a: TeamMember, b: TeamMember) => number;
  renderCell: (m: TeamMember, ctx: ColumnContext) => React.ReactNode;
  renderText: (m: TeamMember, ctx: ColumnContext) => string;
  // Optional print-tailored cell (icons sized for the print sheet). PrintView
  // uses this when present; falls back to renderText. Excel always uses
  // renderText. Only the icon-composite status columns define this so the print
  // shows the same status icons as the on-screen grid.
  renderPrintCell?: (m: TeamMember, ctx: ColumnContext) => React.ReactNode;
  alwaysVisible?: boolean;
  defaultForScopes?: FilterScope[];
  source?: ColumnSource;
  /**
   * When true, the table renders a masked placeholder (••••) for this column
   * whenever the consumer passes maskConfidential={true}. Only applies to the
   * Team grid — the Users tab explicitly unsets this flag because nothing on
   * the Users tab is confidential.
   *
   * Confidential columns: Employee ID, Hire Date, Birthday, Mobile Phone, Email.
   */
  confidential?: boolean;
};

// --- comparator helpers ---
const cmpStr = (a: string | null | undefined, b: string | null | undefined) => (a ?? '').localeCompare(b ?? '');
const cmpDate = (a: string | null | undefined, b: string | null | undefined) => {
  if (!a && !b) return 0;
  if (!a) return 1;
  if (!b) return -1;
  return new Date(a).getTime() - new Date(b).getTime();
};
const cmpBool = (a: boolean, b: boolean) => (a === b ? 0 : a ? -1 : 1);

const STATUS_PRIORITY: Record<TeamMemberStatus, number> = {
  Active: 0,
  Inactive: 1,
  Terminated: 2,
};
const USER_STATUS_PRIORITY: Record<UserLoginStatus, number> = {
  Active: 0,
  Pending: 1,
  Disabled: 2,
};
const ROLE_PRIORITY: Record<TeamMemberRole, number> = {
  'SMS MANAGER': 0,
  'SMS USER': 1,
  'SMS EMPLOYEE': 2,
};

// All team-view drift scopes — for Team columns' default visibility.
// 'drift:staleLogin' is intentionally excluded — Stale Login is not a
// Team-view Issues filter option (see TEAM_DRIFT_KINDS in types.ts).
const ALL_TEAM_DRIFT_SCOPES: FilterScope[] = ['drift:statusMismatch', 'drift:roleMismatch', 'drift:staleSchedule', 'drift:overduePurge'];

// All users-view drift scopes — for Users columns' default visibility.
const ALL_USERS_DRIFT_SCOPES: FilterScope[] = ['drift:staleLogin', 'drift:noPinSet', 'drift:noContactEmail', 'drift:noRecoveryEmail'];

// --- composite Employment Status cell (Team view's first column) ---
// Status pill + login-status icon (ShieldUser, color-coded) + primary star.
//
// The shield-user icon encodes the User record's `status`:
//   - Active   → success green (login works)
//   - Pending  → muted gray    (invited but not activated)
//   - Disabled → error red     (login blocked)
//   - no user  → no icon       (crew, no login expected)
//
// Why this matters: a Terminated employee with a Disabled user is in-sync
// (login correctly blocked, red icon below the gray Terminated pill);
// a Terminated employee with an Active user is drift (green icon under a
// Terminated pill — immediately visible at-scan).
const LOGIN_STATUS_COLOR: Record<UserLoginStatus, string> = {
  Active: 'success.600',
  Pending: 'gray.400',
  Disabled: 'error.500',
};

// Employment-status icon used in the Users view (User Status as primary, this
// as secondary). Active = user-round-check green; Inactive = user-round-x
// gray; Terminated = user-round-x red.
type EmploymentIconSpec = { Icon: React.ComponentType<any>; color: string };
const EMPLOYMENT_STATUS_ICON: Record<TeamMemberStatus, EmploymentIconSpec> = {
  Active: { Icon: UserRoundCheckIcon, color: 'success.600' },
  Inactive: { Icon: UserRoundXIcon, color: 'gray.400' },
  Terminated: { Icon: UserRoundXIcon, color: 'error.500' },
};

const COMPOSITE_ICON_SIZE = 5; // 20px — matches the larger pill text height

// Auto Sync indicator — always rendered so the icon occupies the same
// column position on every row (consistent left-to-right scan).
//   auto      → blue.500   ("On" — healthy default)
//   protected → amber.500  ("Status/Role Only — partial freeze")
//   locked    → error.500  ("Off — fully frozen")
// Icon order in composite cells: AutoSync FIRST, user-record icon SECOND,
// Primary star THIRD — so readers see the sync state before login state.
const AutoSyncIcon: React.FC<{ member: TeamMember; ctx: ColumnContext }> = ({ member, ctx }) => {
  const label =
    member.importSyncMode === 'Locked'
      ? ctx.autoSyncOffLabel
      : member.importSyncMode === 'Protected'
      ? ctx.autoSyncProtectedLabel
      : ctx.autoSyncOnLabel;
  return <RefreshCwIcon size={COMPOSITE_ICON_SIZE} color={importSyncIconColor(member.importSyncMode)} accessibilityLabel={label} />;
};

// Team view: pill → Auto Sync → User login status → Primary star.
// Icon order is fixed so every row reads left-to-right in the same positions.
const EmploymentStatusCell: React.FC<{ member: TeamMember; ctx: ColumnContext }> = ({ member, ctx }) => {
  const s = statusBadgeStyle(member.status);
  const user = member.user;
  return (
    <HStack space={2} alignItems="center">
      <Badge bg={s.bg} text={s.text} label={ctx.statusLabels[member.status]} />
      <AutoSyncIcon member={member} ctx={ctx} />
      {user && (
        <ShieldUserIcon
          size={COMPOSITE_ICON_SIZE}
          color={LOGIN_STATUS_COLOR[user.status]}
          accessibilityLabel={`${ctx.loginBadgeLabel}: ${ctx.userStatusLabels[user.status]}`}
        />
      )}
      {user?.isPrimary && <StarIcon size={COMPOSITE_ICON_SIZE} color="amber.500" accessibilityLabel={ctx.primaryLabel} />}
    </HStack>
  );
};

// Users view: User Status pill, then Employment Status icon, optional
// Primary star for the site's primary manager (usually the GM).
const PrimaryStar: React.FC<{ ctx: ColumnContext }> = ({ ctx }) => (
  <StarIcon size={COMPOSITE_ICON_SIZE} color="amber.500" accessibilityLabel={ctx.primaryLabel} />
);

const UserStatusCell: React.FC<{ member: TeamMember; ctx: ColumnContext }> = ({ member, ctx }) => {
  const user = member.user;
  if (!user) {
    // Users view filters to userId != null upstream, so this is defensive
    // (shouldn't fire in practice).
    return <MutedText>{ctx.emptyValue}</MutedText>;
  }
  const userPill = userStatusBadgeStyle(user.status);
  const { Icon: EmpIcon, color: empColor } = EMPLOYMENT_STATUS_ICON[member.status];
  return (
    <HStack space={2} alignItems="center">
      <Badge bg={userPill.bg} text={userPill.text} label={ctx.userStatusLabels[user.status]} />
      <AutoSyncIcon member={member} ctx={ctx} />
      <EmpIcon size={COMPOSITE_ICON_SIZE} color={empColor} accessibilityLabel={ctx.statusLabels[member.status]} />
      {user.isPrimary && <PrimaryStar ctx={ctx} />}
    </HStack>
  );
};

// Print-sheet status cells — plain text, one status per line, no icons/badges
// (they don't reproduce legibly on paper). Primary is folded into the first
// line as a "(Primary)" suffix rather than its own row. Used by PrintView via
// renderPrintCell.
const PrintTextLine: React.FC<{ children: React.ReactNode; primary?: boolean }> = ({ children, primary = false }) => (
  <Text size={primary ? 'xs' : '2xs'} fontWeight={primary ? 600 : 400} color={primary ? 'gray.900' : 'gray.600'}>
    {children}
  </Text>
);

const EmploymentStatusPrintCell: React.FC<{ member: TeamMember; ctx: ColumnContext }> = ({ member: m, ctx }) => {
  const statusLine = `${ctx.statusLabels[m.status]}${m.user?.isPrimary ? ` (${ctx.primaryLabel})` : ''}`;
  const sync = autoSyncLabel(m, ctx);
  return (
    <VStack space={0.5}>
      <PrintTextLine primary>{statusLine}</PrintTextLine>
      {m.user && <PrintTextLine>{`${ctx.loginBadgeLabel}: ${ctx.userStatusLabels[m.user.status]}`}</PrintTextLine>}
      {sync && <PrintTextLine>{sync}</PrintTextLine>}
    </VStack>
  );
};

const UserStatusPrintCell: React.FC<{ member: TeamMember; ctx: ColumnContext }> = ({ member: m, ctx }) => {
  if (!m.user) return <MutedText>{ctx.emptyValue}</MutedText>;
  const statusLine = `${ctx.userStatusLabels[m.user.status]}${m.user.isPrimary ? ` (${ctx.primaryLabel})` : ''}`;
  const sync = autoSyncLabel(m, ctx);
  return (
    <VStack space={0.5}>
      <PrintTextLine primary>{statusLine}</PrintTextLine>
      <PrintTextLine>{ctx.statusLabels[m.status]}</PrintTextLine>
      {sync && <PrintTextLine>{sync}</PrintTextLine>}
    </VStack>
  );
};

// Plain-text projection for the composite status cells' Auto Sync line.
const autoSyncLabel = (m: TeamMember, ctx: ColumnContext): string | null => {
  if (m.importSyncMode === 'Auto') return null;
  const stateLabel = m.importSyncMode === 'Locked' ? ctx.autoSyncOffLabel : ctx.autoSyncProtectedLabel;
  return `${ctx.autoSyncPrefixLabel}: ${stateLabel}`;
};

// Plain single-value text for Excel — the composite badge+icon on-screen cell
// (renderCell) and the print composite cell (renderPrintCell) are unchanged;
// this only affects the Excel text projection. Sync mode / Primary each get
// their own standalone Excel-only column below (COL_SYNC_MODE, COL_IS_PRIMARY)
// so nothing is lost and no field is duplicated across two columns.
const employmentStatusText = (m: TeamMember, ctx: ColumnContext): string => ctx.statusLabels[m.status];

const userStatusText = (m: TeamMember, ctx: ColumnContext): string => (m.user ? ctx.userStatusLabels[m.user.status] : '');

// --- shared column definitions (used by both Team and Users views) ---

const COL_EMPLOYMENT_STATUS: ColumnDef = {
  id: 'employmentStatus',
  labelKey: 'store_settings:employment_status',
  width: 200,
  sortFn: (a, b) => STATUS_PRIORITY[a.status] - STATUS_PRIORITY[b.status],
  renderCell: (m, ctx) => <EmploymentStatusCell member={m} ctx={ctx} />,
  renderText: employmentStatusText,
  renderPrintCell: (m, ctx) => <EmploymentStatusPrintCell member={m} ctx={ctx} />,
  alwaysVisible: true,
};

// Users view first column — User Status pill primary, Employment Status as
// the secondary icon. Same shape as COL_EMPLOYMENT_STATUS but the two roles
// are flipped (login is primary; employment is the at-a-scan signal).
const COL_USER_STATUS_PRIMARY: ColumnDef = {
  id: 'userStatusPrimary',
  labelKey: 'store_settings:user_status',
  width: 200,
  source: 'user',
  sortFn: (a, b) => {
    if (!a.user && !b.user) return 0;
    if (!a.user) return 1;
    if (!b.user) return -1;
    return USER_STATUS_PRIORITY[a.user.status] - USER_STATUS_PRIORITY[b.user.status];
  },
  renderCell: (m, ctx) => <UserStatusCell member={m} ctx={ctx} />,
  renderText: userStatusText,
  renderPrintCell: (m, ctx) => <UserStatusPrintCell member={m} ctx={ctx} />,
  alwaysVisible: true,
};

// Name + Job Title pull from the EMPLOYEE record (m.firstName / m.lastName /
// m.title) on BOTH views. The API's user object carries no name/title of its
// own, so the Employee record is the single source — there is no user-side
// copy to drift against.
const COL_NAME: ColumnDef = {
  id: 'name',
  labelKey: 'store_settings:name',
  width: 180,
  sortFn: (a, b) => cmpStr(a.lastName, b.lastName) || cmpStr(a.firstName, b.firstName),
  renderCell: m => (
    <RowText bold>
      {m.firstName} {m.lastName}
    </RowText>
  ),
  renderText: m => `${m.firstName} ${m.lastName}`,
  alwaysVisible: true,
};

const COL_JOB_TITLE: ColumnDef = {
  id: 'jobTitle',
  labelKey: 'store_settings:job_title',
  width: 150,
  sortFn: (a, b) => cmpStr(a.title, b.title),
  renderCell: m => <RowText>{m.title || '—'}</RowText>,
  renderText: m => m.title || '',
  alwaysVisible: true,
};

const COL_EMPLOYEE_ID: ColumnDef = {
  id: 'employeeId',
  labelKey: 'store_settings:employee_id',
  width: 110,
  sortFn: (a, b) => cmpStr(a.employeeNum, b.employeeNum),
  renderCell: m => <RowText>{m.employeeNum || '—'}</RowText>,
  renderText: m => m.employeeNum || '',
  alwaysVisible: true,
  confidential: true,
};

const COL_ROLE: ColumnDef = {
  id: 'role',
  labelKey: 'store_settings:role',
  width: 130,
  sortFn: (a, b) => ROLE_PRIORITY[a.role] - ROLE_PRIORITY[b.role],
  renderCell: (m, ctx) => {
    const s = roleBadgeStyle(m.role);
    return <Badge bg={s.bg} text={s.text} label={ctx.roleLabels[m.role]} />;
  },
  renderText: (m, ctx) => ctx.roleLabels[m.role],
  alwaysVisible: true,
};

// Excel-only supplemental columns. These three are NOT part of
// TEAM_COLUMNS/USERS_COLUMNS and never appear in the Columns popover, the
// grid, or Print — the entry screen's Excel handler appends them directly to
// whichever columns it already built, for both view scopes, regardless of any
// Columns-popover toggle state (there's no UI toggle for them). Previously
// these three fields only existed embedded inside the Employment/User Status
// composite text, or (Primary) only as an inline star icon with no text form
// at all — making them impossible to sort/filter on independently in Excel.
const SYNC_MODE_PRIORITY: Record<ImportSyncMode, number> = {
  Auto: 0,
  Protected: 1,
  Locked: 2,
};
// Shared by both views — sync mode is an Employee-record field, not view-
// specific. Unlike autoSyncLabel() above (which hides the default "Auto"
// state to avoid noise on a compact composite line), this column always shows
// a value — a real column with no value defeats sorting/filtering.
export const COL_SYNC_MODE: ColumnDef = {
  id: 'syncMode',
  labelKey: 'store_settings:auto_sync',
  width: 150,
  sortFn: (a, b) => SYNC_MODE_PRIORITY[a.importSyncMode] - SYNC_MODE_PRIORITY[b.importSyncMode],
  renderCell: (m, ctx) => (
    <RowText>
      {m.importSyncMode === 'Locked'
        ? ctx.autoSyncOffLabel
        : m.importSyncMode === 'Protected'
        ? ctx.autoSyncProtectedLabel
        : ctx.autoSyncOnLabel}
    </RowText>
  ),
  renderText: (m, ctx) =>
    m.importSyncMode === 'Locked'
      ? ctx.autoSyncOffLabel
      : m.importSyncMode === 'Protected'
      ? ctx.autoSyncProtectedLabel
      : ctx.autoSyncOnLabel,
};

// Shared by both views; sources from the linked User record (isPrimary
// lives on User, not Employee).
export const COL_IS_PRIMARY: ColumnDef = {
  id: 'isPrimary',
  labelKey: 'store_settings:primary',
  width: 100,
  source: 'user',
  sortFn: (a, b) => cmpBool(!!a.user?.isPrimary, !!b.user?.isPrimary),
  renderCell: (m, ctx) => <YesNoBadge value={!!m.user?.isPrimary} yesLabel={ctx.yesLabel} noLabel={ctx.noLabel} />,
  renderText: (m, ctx) => (m.user?.isPrimary ? ctx.yesLabel : ctx.noLabel),
};

// Users-view only — Team view's own COL_EMPLOYMENT_STATUS already covers this
// field for Team's Excel export (appending this too would duplicate the same
// field under the same label).
export const COL_EMPLOYMENT_STATUS_USERS: ColumnDef = {
  id: 'employmentStatusUsers',
  labelKey: 'store_settings:employment_status',
  width: 130,
  sortFn: (a, b) => STATUS_PRIORITY[a.status] - STATUS_PRIORITY[b.status],
  renderCell: (m, ctx) => {
    const s = statusBadgeStyle(m.status);
    return <Badge bg={s.bg} text={s.text} label={ctx.statusLabels[m.status]} />;
  },
  renderText: (m, ctx) => ctx.statusLabels[m.status],
};

// --- dynamic columns ---

const COL_USER_STATUS: ColumnDef = {
  id: 'userStatus',
  labelKey: 'store_settings:user_status',
  width: 130,
  source: 'user',
  sortFn: (a, b) => {
    if (!a.user && !b.user) return 0;
    if (!a.user) return 1;
    if (!b.user) return -1;
    return USER_STATUS_PRIORITY[a.user.status] - USER_STATUS_PRIORITY[b.user.status];
  },
  renderCell: (m, ctx) => {
    if (!m.user) return <MutedText>{ctx.emptyValue}</MutedText>;
    const s = userStatusBadgeStyle(m.user.status);
    return <Badge bg={s.bg} text={s.text} label={ctx.userStatusLabels[m.user.status]} />;
  },
  renderText: (m, ctx) => (m.user ? ctx.userStatusLabels[m.user.status] : ''),
  // 'active' excluded from defaults (the shield-user icon next to Employee
  // Status already conveys it). Also explicitly excluded from
  // `drift:staleSchedule` — there, the diagnostic signal is the schedule
  // date, not the login state. All other drift scopes keep it for context.
  defaultForScopes: ['notActive', 'drift:statusMismatch', 'drift:roleMismatch', 'drift:overduePurge', ...ALL_USERS_DRIFT_SCOPES],
};

const COL_USER_ROLE: ColumnDef = {
  id: 'userRole',
  labelKey: 'store_settings:user_role',
  width: 130,
  source: 'user',
  sortFn: (a, b) => {
    if (!a.user && !b.user) return 0;
    if (!a.user) return 1;
    if (!b.user) return -1;
    return ROLE_PRIORITY[a.user.userRole] - ROLE_PRIORITY[b.user.userRole];
  },
  renderCell: (m, ctx) => {
    if (!m.user) return <MutedText>{ctx.emptyValue}</MutedText>;
    const s = roleBadgeStyle(m.user.userRole);
    return <Badge bg={s.bg} text={s.text} label={ctx.roleLabels[m.user.userRole]} />;
  },
  renderText: (m, ctx) => (m.user ? ctx.roleLabels[m.user.userRole] : ''),
  defaultForScopes: ['drift:roleMismatch'],
};

// Users-view variant — alwaysVisible. Same content as COL_USER_ROLE; Team
// view keeps the original (dynamic) variant.
const COL_USER_ROLE_USERS: ColumnDef = {
  ...COL_USER_ROLE,
  alwaysVisible: true,
};

const COL_STATUS_UPDATED: ColumnDef = {
  id: 'statusUpdated',
  labelKey: 'store_settings:status_updated',
  width: 140,
  sortFn: (a, b) => cmpDate(a.statusDateUtc, b.statusDateUtc),
  // Red when hasOverduePurge — Terminated 30+ days with sync not Locked. Never
  // colors for Active/Inactive rows; that's the point (this field is
  // diagnosing overdue purge candidates specifically, not staleness in general).
  renderCell: (m, ctx) => (
    <RowText danger={hasOverduePurge(m, ctx.utcOffsetInMinutes)}>
      {formatRelativeDays(m.statusDateUtc, ctx.translate, ctx.utcOffsetInMinutes)}
    </RowText>
  ),
  renderText: m => formatDateShort(m.statusDateUtc),
  // Critical for Not Active (when did this person leave?) and for the
  // Overdue Purge view (how long past 30 days?).
  defaultForScopes: ['notActive', 'drift:statusMismatch', 'drift:overduePurge'],
};

// Exit Date / Exit Reason columns intentionally omitted — neither field
// exists on the Employee entity. StatusDateUtc (surfaced via the Status
// Updated column) already conveys "when this person was terminated" for
// Terminated rows.

const COL_BIRTHDAY: ColumnDef = {
  id: 'birthday',
  labelKey: 'store_settings:birthday',
  width: 100,
  sortFn: (a, b) => cmpStr(a.birthDate, b.birthDate),
  renderCell: m => <RowText>{formatBirthday(m.birthDate)}</RowText>,
  renderText: m => formatBirthday(m.birthDate),
  defaultForScopes: ['active'],
  confidential: true,
};

const COL_HIRE_DATE: ColumnDef = {
  id: 'hireDate',
  labelKey: 'store_settings:hire_date',
  width: 120,
  sortFn: (a, b) => cmpDate(a.hireDate, b.hireDate),
  renderCell: m => <RowText>{formatDateShort(m.hireDate)}</RowText>,
  renderText: m => formatDateShort(m.hireDate),
  defaultForScopes: ['active', 'drift:staleSchedule'],
  confidential: true,
};

const COL_LAST_SCHEDULED: ColumnDef = {
  id: 'lastScheduled',
  labelKey: 'store_settings:last_scheduled',
  width: 130,
  sortFn: (a, b) => cmpDate(a.lastScheduledDateUtc, b.lastScheduledDateUtc),
  // Red when hasStaleSchedule — active employee not scheduled in 30+ days (or
  // never scheduled).
  renderCell: (m, ctx) => (
    <RowText danger={hasStaleSchedule(m, ctx.utcOffsetInMinutes)}>
      {formatRelativeDays(m.lastScheduledDateUtc, ctx.translate, ctx.utcOffsetInMinutes)}
    </RowText>
  ),
  // renderText feeds Excel + print: absolute date, not the on-screen grid's
  // relative "Xd ago" — a printed/exported report gets reviewed later, when
  // "2w ago" no longer means anything.
  renderText: m => formatDateShort(m.lastScheduledDateUtc),
  defaultForScopes: ['active', 'drift:staleSchedule'],
};

const COL_MOBILE_PHONE: ColumnDef = {
  id: 'mobilePhone',
  labelKey: 'store_settings:mobile_phone',
  width: 130,
  sortFn: (a, b) => cmpStr(a.mobileNumber, b.mobileNumber),
  renderCell: m => <RowText>{m.mobileNumber || '—'}</RowText>,
  renderText: m => m.mobileNumber || '',
  defaultForScopes: ['active'],
  confidential: true,
};

// Audit columns — Modified / Modified By on the Employee record. Hidden by
// default (no `defaultForScopes`) but available via the Columns popover.
// Team view only — these aren't on the User record.
const COL_LAST_MODIFIED: ColumnDef = {
  id: 'lastModified',
  labelKey: 'store_settings:modified',
  width: 130,
  sortFn: (a, b) => cmpDate(a.lastModifiedOnUtc, b.lastModifiedOnUtc),
  renderCell: (m, ctx) => <RowText>{formatRelativeDays(m.lastModifiedOnUtc, ctx.translate, ctx.utcOffsetInMinutes)}</RowText>,
  renderText: m => formatDateShort(m.lastModifiedOnUtc),
};

const COL_LAST_MODIFIED_BY: ColumnDef = {
  id: 'lastModifiedBy',
  labelKey: 'store_settings:modified_by',
  width: 140,
  sortFn: (a, b) => cmpStr(a.lastModifiedBy, b.lastModifiedBy),
  renderCell: (m, ctx) => (m.lastModifiedBy ? <RowText>{m.lastModifiedBy}</RowText> : <MutedText>{ctx.emptyValue}</MutedText>),
  renderText: m => m.lastModifiedBy ?? '',
};

const COL_CONTACT_EMAIL: ColumnDef = {
  id: 'contactEmail',
  labelKey: 'store_settings:email',
  width: 150,
  sortFn: (a, b) => cmpStr(a.email, b.email),
  // Employee.email and User.email are fully decoupled — no fallback. This
  // column shows ONLY the Employee record's email; the User record's email
  // has its own "Contact Email" column on the Users page.
  renderCell: m => <RowText>{m.email || '—'}</RowText>,
  renderText: m => m.email || '',
  defaultForScopes: ['active', 'drift:noContactEmail'],
  confidential: true,
};

// Primary is surfaced only as an inline star icon in the Employee Status
// (Team view) and User Status (Users view) cells — no standalone column.

// Users-view variants of three columns that need different alwaysVisible
// treatment than Team view:
//   - Username  → alwaysVisible on Users view (it's the User record's
//                 primary identifier); dynamic on Team view (where the
//                 row is keyed by Employee Number instead).
//   - User Role → alwaysVisible on Users view (Users tab's primary role
//                 signal); dynamic on Team view (Employee Role is the
//                 primary there).
//   - Employee Role → NOT alwaysVisible on Users view; still alwaysVisible
//                     on Team view. Surfaces in the Role Mismatch drift on
//                     Users view for comparison.
// Same renderCell / sortFn / source as the base definitions.

const COL_ROLE_USERS: ColumnDef = {
  ...COL_ROLE,
  alwaysVisible: false,
  defaultForScopes: ['drift:roleMismatch'],
};

// Users-view variant — Employee Id is dropped from the alwaysVisible set on
// Users view. Still in the column list (togglable via the Columns popover)
// but hidden by default everywhere — managers rarely need the numeric
// employee id when scanning login records. confidential: false — the Users
// tab has no confidential columns.
const COL_EMPLOYEE_ID_USERS: ColumnDef = {
  ...COL_EMPLOYEE_ID,
  alwaysVisible: false,
  confidential: false,
};

const COL_USERNAME: ColumnDef = {
  id: 'username',
  labelKey: 'store_settings:username',
  width: 170,
  source: 'user',
  sortFn: (a, b) => cmpStr(a.user?.userName, b.user?.userName),
  renderCell: m => <RowText bold>{m.user?.userName ?? '—'}</RowText>,
  renderText: m => m.user?.userName ?? '',
  defaultForScopes: ['active', 'notActive', ...ALL_TEAM_DRIFT_SCOPES, ...ALL_USERS_DRIFT_SCOPES],
};

// Users-view variant — alwaysVisible. The User record's primary identifier
// on the Users tab.
const COL_USERNAME_USERS: ColumnDef = {
  ...COL_USERNAME,
  alwaysVisible: true,
};

// Users view's Contact Email column — User.email. The manager-entered email
// for product comms. Fully independent of Employee.email (Team view's
// "Email" column).
//
// Grid shows Yes/No only — the actual address is confidential and only
// visible via the (deferred) Employee Profile modal. This mirrors PIN Set:
// the column confirms presence, not the value.
const COL_USER_CONTACT_EMAIL: ColumnDef = {
  id: 'userContactEmail',
  labelKey: 'store_settings:user_contact_email',
  width: 110,
  source: 'user',
  sortFn: (a, b) => cmpBool(!!a.user?.email, !!b.user?.email),
  renderCell: (m, ctx) => <YesNoBadge value={!!m.user?.email} yesLabel={ctx.yesLabel} noLabel={ctx.noLabel} />,
  renderText: (m, ctx) => (m.user?.email ? ctx.yesLabel : ctx.noLabel),
  // Not Active hidden by default — once a login is disabled, contact email is
  // no longer actionable; the diagnostic signal lives on the
  // drift:noContactEmail filter instead.
  defaultForScopes: ['active', 'drift:noContactEmail'],
};

// Users view's Recovery Email column — User.recoveryEmail, used for password
// reset (NOT the same as Contact Email). "Yes" only when the address is
// present AND verified; everything else — missing, pending, or expired —
// reads "No". An unverified address can't be used for a reset, so it doesn't
// count as having a recovery email.
//
// Grid shows Yes/No only — same privacy rationale as Contact Email above.
// RECOVERY_EMAIL_STATUS_VERIFIED (types.ts) is a confirmed string literal
// ('Verified') — see types.ts comment for the real-payload confirmation.
const hasVerifiedRecoveryEmail = (m: TeamMember): boolean =>
  !!m.user?.recoveryEmail && m.user?.recoveryEmailStatus === RECOVERY_EMAIL_STATUS_VERIFIED;

const COL_USER_RECOVERY_EMAIL: ColumnDef = {
  id: 'userRecoveryEmail',
  labelKey: 'store_settings:recovery_email',
  width: 110,
  source: 'user',
  sortFn: (a, b) => cmpBool(hasVerifiedRecoveryEmail(a), hasVerifiedRecoveryEmail(b)),
  renderCell: (m, ctx) => <YesNoBadge value={hasVerifiedRecoveryEmail(m)} yesLabel={ctx.yesLabel} noLabel={ctx.noLabel} />,
  renderText: (m, ctx) => (hasVerifiedRecoveryEmail(m) ? ctx.yesLabel : ctx.noLabel),
  defaultForScopes: ['active', 'drift:noRecoveryEmail'],
};

const COL_PIN_SET: ColumnDef = {
  id: 'pinSet',
  labelKey: 'store_settings:pin_set',
  width: 100,
  source: 'user',
  sortFn: (a, b) => cmpBool(!!a.user?.pinSet, !!b.user?.pinSet),
  renderCell: (m, ctx) => <YesNoBadge value={!!m.user?.pinSet} yesLabel={ctx.yesLabel} noLabel={ctx.noLabel} />,
  renderText: (m, ctx) => (m.user?.pinSet ? ctx.yesLabel : ctx.noLabel),
  defaultForScopes: ['active', 'drift:noPinSet'],
};

const COL_LAST_LOGIN: ColumnDef = {
  id: 'lastLogin',
  labelKey: 'store_settings:last_login',
  width: 130,
  source: 'user',
  sortFn: (a, b) => cmpDate(a.user?.lastLoginDateUtc, b.user?.lastLoginDateUtc),
  // An employee with no linked user at all can't log in, so this must read
  // "—" (like the print view does), not "Never" (which implies they have
  // login capability but haven't used it). Red when hasStaleLogin — active
  // login, no sign-in in 30+ days, or never logged in despite having an
  // active login.
  renderCell: (m, ctx) => (
    <RowText danger={hasStaleLogin(m, ctx.utcOffsetInMinutes)}>
      {m.user ? formatRelativeDays(m.user.lastLoginDateUtc, ctx.translate, ctx.utcOffsetInMinutes) : '—'}
    </RowText>
  ),
  // renderText feeds Excel + print: absolute date, not the on-screen grid's
  // relative "Xd ago" — matches COL_STATUS_UPDATED.
  renderText: m => (m.user ? formatDateShort(m.user.lastLoginDateUtc) : '—'),
  defaultForScopes: ['active', 'notActive', ...ALL_USERS_DRIFT_SCOPES],
};

// Team-view variant — Last Login is not default-visible on Team view except
// on Status Mismatch specifically (where "did this person actually still
// have login access?" is directly the question being diagnosed). Still
// toggleable via the Columns popover on Team view like any other optional
// column — only the default changed. Users view (COL_LAST_LOGIN above) is
// untouched.
const COL_LAST_LOGIN_TEAM: ColumnDef = {
  ...COL_LAST_LOGIN,
  defaultForScopes: ['drift:statusMismatch'],
};

// --- Team-view columns ---
// First 5 are always visible (Status, Name, Job Title, EmployeeId, Role).
// Status uses the composite cell with inline sync + login icons.
//
// Column order beyond the alwaysVisible 5 groups by concern:
//   1. Employee-record context — Employee Status Updated
//   2. User-record context — User Role, User Status
//   3. Activity — Last Login, Last Scheduled
//   4. Personal — Hire Date, Birthday
//   5. Contact — Mobile Phone, Contact Email
export const TEAM_COLUMNS: ColumnDef[] = [
  COL_EMPLOYMENT_STATUS,
  COL_NAME,
  COL_JOB_TITLE,
  COL_EMPLOYEE_ID,
  COL_ROLE,
  // Dynamic — scope-driven default visibility.
  COL_STATUS_UPDATED,
  COL_USER_ROLE,
  COL_USER_STATUS,
  COL_LAST_LOGIN_TEAM,
  COL_LAST_SCHEDULED,
  COL_HIRE_DATE,
  COL_BIRTHDAY,
  COL_MOBILE_PHONE,
  COL_CONTACT_EMAIL,
  // Audit columns last — hidden by default, available via Columns popover.
  COL_LAST_MODIFIED,
  COL_LAST_MODIFIED_BY,
];

// --- Users-view columns ---
// Same 5 always-visible header columns; then Users-specific dynamic columns
// emphasizing login health.
export const USERS_COLUMNS: ColumnDef[] = [
  // Users-view alwaysVisible set:
  // User Status · Name · Job Title · Employee Id · Username · User Role.
  // Employee Role is no longer in the static set — demoted to COL_ROLE_USERS,
  // default-shown only on the Role Mismatch drift where the side-by-side
  // comparison with User Role matters. Name and Job Title source from the
  // Employee record (the API user object has no name/title) — same columns as
  // the Team view.
  COL_USER_STATUS_PRIMARY,
  COL_NAME,
  COL_JOB_TITLE,
  COL_EMPLOYEE_ID_USERS,
  COL_USERNAME_USERS,
  COL_USER_ROLE_USERS,
  // Dynamic — scope-driven default visibility.
  COL_ROLE_USERS,
  COL_STATUS_UPDATED,
  // PIN Set positioned above Contact Email — the credential-readiness signal
  // groups closer to Username / Role.
  COL_PIN_SET,
  // Contact Email = User.email and Recovery Email = User.recoveryEmail are
  // two distinct User-record fields. Users view only; the Team view's Email
  // column reads Employee.email and is fully independent.
  COL_USER_CONTACT_EMAIL,
  COL_USER_RECOVERY_EMAIL,
  COL_LAST_LOGIN,
];

// --- default visibility per scope ---
// Computes the set of column IDs that should be visible by default for the
// given (column-set, scope). The screen calls this when the filter changes
// to reset the user's visibility toggles to the scope's defaults.
export const defaultVisibleIdsForScope = (columns: ColumnDef[], scope: FilterScope): string[] => {
  return columns.filter(c => c.alwaysVisible || (c.defaultForScopes && c.defaultForScopes.includes(scope))).map(c => c.id);
};

// --- sort state model ---
export type SortDir = 'asc' | 'desc';
export type SortKey = { id: string; dir: SortDir };

export const applySorts = (members: TeamMember[], sortKeys: SortKey[], columns: ColumnDef[]): TeamMember[] => {
  if (sortKeys.length === 0) {
    return [...members].sort((a, b) => {
      const r = ROLE_PRIORITY[a.role] - ROLE_PRIORITY[b.role];
      if (r !== 0) return r;
      return cmpStr(a.firstName, b.firstName) || cmpStr(a.lastName, b.lastName);
    });
  }
  return [...members].sort((a, b) => {
    for (const sk of sortKeys) {
      const col = columns.find(c => c.id === sk.id);
      if (!col?.sortFn) continue;
      const cmp = col.sortFn(a, b);
      if (cmp !== 0) return sk.dir === 'asc' ? cmp : -cmp;
    }
    return 0;
  });
};

export const cycleSort = (current: SortKey[], columnId: string, shiftHeld: boolean): SortKey[] => {
  const idx = current.findIndex(s => s.id === columnId);
  if (!shiftHeld) {
    if (idx === -1) return [{ id: columnId, dir: 'asc' }];
    const next: SortDir | null = current[idx].dir === 'asc' ? 'desc' : current[idx].dir === 'desc' ? null : 'asc';
    return next == null ? [] : [{ id: columnId, dir: next }];
  }
  if (idx === -1) return [...current, { id: columnId, dir: 'asc' }];
  const next: SortDir | null = current[idx].dir === 'asc' ? 'desc' : current[idx].dir === 'desc' ? null : 'asc';
  const copy = current.slice();
  if (next == null) copy.splice(idx, 1);
  else copy[idx] = { id: columnId, dir: next };
  return copy;
};
